import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle
} from 'reactstrap';

import { messages } from '../../../../_shared/i18n/messages';

import { FIELD_TYPES, FORM_TYPES } from '../../../constants';

import './FormEditorQuestionDropdownMenu.scss';

const getFieldTypeOptions = (intl) => {
	return {
		[FIELD_TYPES.CHECKBOX]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_CHECKBOX),
			icon: 'fa-check-square',
		},
		[FIELD_TYPES.CHECKBOXES]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_CHECKBOXES),
			icon: 'fa-check-square',
		},
		[FIELD_TYPES.EMAIL]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_EMAIL),
			icon: 'fa-envelope',
		},
		[FIELD_TYPES.FILE_UPLOAD]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_FILE_UPLOAD),
			icon: 'fa-file'
		},
		[FIELD_TYPES.LIKERT]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_LIKERT_SCALE),
			icon: 'fa-ellipsis'
		},
		[FIELD_TYPES.LOCATION]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_LOCATION),
			icon: 'fa-map'
		},
		[FIELD_TYPES.RADIO]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_RADIO),
			icon: 'fa-dot-circle',
		},
		[FIELD_TYPES.SECTION]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SECTION),
			icon: 'fa-heading',
		},
		[FIELD_TYPES.SELECT]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SELECT),
			icon: 'fa-chevron-circle-down',
		},
		[FIELD_TYPES.SOCIAL_MEDIA]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SOCIAL_MEDIA),
			icon: 'fa-users',
		},
		[FIELD_TYPES.TEXT]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_TEXT),
			icon: 'fa-grip-lines',
		},
		[FIELD_TYPES.TEXT_AREA]: {
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_TEXTAREA),
			icon: 'fa-align-justify',
		},
	};
};

const propTypes = {

	/**
	 * The form type.
	 */
	type: PropTypes.oneOf([
		FORM_TYPES.APPLICATION,
		FORM_TYPES.FORM,
		FORM_TYPES.POLL,
		FORM_TYPES.POST_REGISTRATION,
		FORM_TYPES.PROFILE,
		FIELD_TYPES.SECTION,
		FORM_TYPES.QUIZ,
	]).isRequired,

	/**
	 * Flag indicating if the question dropdown is nested within
	 * a form section.
	 */
	section: PropTypes.bool,

	/**
	 * Callback function to handle adding a new question of a given type.
	 */
	onAddQuestion: PropTypes.func.isRequired,

	/**
	 * Callback function to handle adding a new section label.
	 */
	onAddSection: PropTypes.func,

};

/**
 * Renders a dropdown item for a type of question.
 *
 * @param   {string} type         The type of question.
 * @param   {array} options       The options list of all possible question types.
 * @param   {func} onClick        Callback function to handle clicking the item.
 * @returns {node}                The react component.
 */
const renderItem = (type, options, onClick) => {

	const questionType = options[type];

	if (!questionType) {
		return null;
	}

	return (
		<DropdownItem
			key={type}
			onClick={() => onClick(type)}>
			<i className={`far ${questionType.icon}`}></i>
			{' '}
			<span>{questionType.label}</span>
		</DropdownItem>
	);

};

/**
 * Renders the default menu items for a form.
 *
 * @param   {array}   options        The options list of all possible question types.
 * @param   {boolean} section        Flag indicating if dropdown is nested under form section.
 * @param   {func}    onAddQuestion  Callback function to handle adding a new question of a
 *                                   given type.
 * @param   {func}    onAddSection   Callback function to handle adding a new section label.
 *
 * @returns {node}                The react component.
 */
const renderDefaultMenuItems = (options, section, onAddQuestion, onAddSection) => {

	const group1 = [
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
		FIELD_TYPES.EMAIL,
		FIELD_TYPES.LOCATION,
	];

	const group2 = [
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.CHECKBOX,
		FIELD_TYPES.CHECKBOXES,
	];

	const group3 = [
		FIELD_TYPES.LIKERT,
		FIELD_TYPES.SOCIAL_MEDIA,
	];

	const group4 = (onAddSection && !section) ? [FIELD_TYPES.SECTION] : null;

	return (
		<Fragment>
			{group1.map((type) => renderItem(type, options, onAddQuestion))}
			{group2 &&
				<Fragment>
					<DropdownItem divider />
					{group2.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
			{group3 &&
				<Fragment>
					<DropdownItem divider />
					{group3.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
			{group4 &&
				<Fragment>
					<DropdownItem divider />
					{group4.map((type) => renderItem(type, options, onAddSection))}
				</Fragment>
			}
		</Fragment>
	);

};

/**
 * Renders the dropdown menu items for a form.
 *
 * @param   {array}   options        The options list of all possible question types.
 * @param   {boolean} section        Flag indicating if dropdown is nested under form section.
 * @param   {func}    onAddQuestion  Callback function to handle adding a new question of a
 *                                   given type.
 * @param   {func}    onAddSection   Callback function to handle adding a new section label.
 *
 * @returns {node}                The react component.
 */
const renderFormMenuItems = (options, section, onAddQuestion, onAddSection) => {

	const group1 = [
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
		FIELD_TYPES.EMAIL,
	];

	const group2 = [
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.CHECKBOXES,
	];

	const group3 = [
		FIELD_TYPES.FILE_UPLOAD,
		FIELD_TYPES.LIKERT,
	];

	const group4 = (onAddSection && !section) ? [FIELD_TYPES.SECTION] : null;

	return (
		<Fragment>
			{group1.map((type) => renderItem(type, options, onAddQuestion))}
			{group2 &&
				<Fragment>
					<DropdownItem divider />
					{group2.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
			{group3 &&
				<Fragment>
					<DropdownItem divider />
					{group3.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
			{group4 &&
				<Fragment>
					<DropdownItem divider />
					{group4.map((type) => renderItem(type, options, onAddSection))}
				</Fragment>
			}
		</Fragment>
	);

};

/**
 * Renders the dropdown menu items for a poll.
 *
 * @param   {array}   options        The options list of all possible question types.
 * @param   {boolean} section        Flag indicating if dropdown is nested under form section.
 * @param   {func}    onAddQuestion  Callback function to handle adding a new question of a
 *                                   given type.
 * @param   {func}    onAddSection   Callback function to handle adding a new section label.
 *
 * @returns {node}                The react component.
 */
const renderPollMenuItems = (options, section, onAddQuestion, onAddSection) => {

	const group1 = [
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
	];

	const group2 = [FIELD_TYPES.LIKERT];

	const group3 = (onAddSection && !section) ? [FIELD_TYPES.SECTION] : null;

	return (
		<Fragment>
			{group1.map((type) => renderItem(type, options, onAddQuestion))}
			{group2 &&
				<Fragment>
					<DropdownItem divider />
					{group2.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
			{group3 &&
				<Fragment>
					<DropdownItem divider />
					{group3.map((type) => renderItem(type, options, onAddSection))}
				</Fragment>
			}
		</Fragment>
	);

};

/**
 * Renders the dropdown menu items for a quiz.
 *
 * @param   {array}   options        The options list of all possible question types.
 * @param   {boolean} section        Flag indicating if dropdown is nested under form section.
 * @param   {func}    onAddQuestion  Callback function to handle adding a new question of a
 *                                   given type.
 *
 * @returns {node}                The react component.
 */
const renderQuizMenuItems = (options, section, onAddQuestion) => {

	const group1 = [
		FIELD_TYPES.RADIO,
		FIELD_TYPES.CHECKBOXES,
	];

	const group2 = [
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
		FIELD_TYPES.LIKERT,
	];

	return (
		<Fragment>
			{group1.map((type) => renderItem(type, options, onAddQuestion))}
			{group2 &&
				<Fragment>
					<DropdownItem divider />
					{group2.map((type) => renderItem(type, options, onAddQuestion))}
				</Fragment>
			}
		</Fragment>
	);

};

/**
 * Renders the dropdown menu items for an application form.
 *
 * @param   {array} options       The options list of all possible question types.
 * @param   {boolean} section        Flag indicating if dropdown is nested under form section.
 * @param   {func} onAddQuestion  Callback function to handle adding a new question of a given type.
 * @param   {func} onAddSection   Callback function to handle adding a new section label.
 * @returns {node}                The react component.
 */
const renderApplicationFormMenuItems = (options, section, onAddQuestion, onAddSection) => {

	const group1 = [
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
		FIELD_TYPES.EMAIL,
		FIELD_TYPES.LOCATION,
	];

	const group2 = [
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.CHECKBOX,
		FIELD_TYPES.CHECKBOXES,
	];

	const group3 = [
		FIELD_TYPES.LIKERT,
		FIELD_TYPES.SOCIAL_MEDIA,
		FIELD_TYPES.FILE_UPLOAD,
	];

	const group4 = (onAddSection && !section) ? [FIELD_TYPES.SECTION] : null;

	return (
		<Fragment>
			{group1.map((type) => renderItem(type, options, onAddQuestion))}
			<DropdownItem divider />
			{group2 && group2.map((type) => renderItem(type, options, onAddQuestion))}
			<DropdownItem divider />
			{group3 && group3.map((type) => renderItem(type, options, onAddQuestion))}
			<DropdownItem divider />
			{group4 && group4.map((type) => renderItem(type, options, onAddSection))}
		</Fragment>
	);

};

const FormEditorQuestionDropdownMenu = ({ type, section, onAddQuestion, onAddSection }) => {

	const intl = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const options = getFieldTypeOptions(intl);

	let prompt;

	let menuItems;

	if (type === FORM_TYPES.APPLICATION) {

		menuItems = renderApplicationFormMenuItems(options, section, onAddQuestion, onAddSection);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD);

	} else if (type === FORM_TYPES.FORM) {

		menuItems = renderFormMenuItems(options, section, onAddQuestion, onAddSection);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD);

	} else if (type === FORM_TYPES.POLL) {

		menuItems = renderPollMenuItems(options, section, onAddQuestion, onAddSection);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD);

	} else if (type === FORM_TYPES.POST_REGISTRATION) {

		menuItems = renderDefaultMenuItems(options, section, onAddQuestion, onAddSection);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD);

	} else if (type === FORM_TYPES.PROFILE) {

		menuItems = renderDefaultMenuItems(options, section, onAddQuestion, onAddSection);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD);

	} else if (type === FORM_TYPES.QUIZ) {

		menuItems = renderQuizMenuItems(options, section, onAddQuestion);
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD_NEW_QUESTION_TO_QUIZ);

	} else {
		console.warn(`The form type=${type} is not supported in the question dropdown menu...`);
	}

	if (section) {
		prompt = intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD_NEW_QUESTION_TO_SECTION);
	}

	return (
		<Dropdown
			className="tc-form-editor-question-dropdown"
			isOpen={isOpen}
			toggle={() => setIsOpen(!isOpen)}>
			<DropdownToggle
				className="fw-normal text-muted"
				color="neutral">
				<span>
					<i
						className="fal fa-circle-plus"
						aria-hidden="true" />
					{' '}
					{prompt}
				</span>
			</DropdownToggle>
			<DropdownMenu>
				{menuItems}
			</DropdownMenu>
		</Dropdown>
	);

};

FormEditorQuestionDropdownMenu.propTypes = propTypes;

export default FormEditorQuestionDropdownMenu;
